import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router';
import { Card, CardContent, cn, Skeleton } from '@brimostech/ui';
import { formatNumber } from '@/lib/format';

interface StatCardProps {
  label: string;
  /** A number is formatted (1.234); a string (money) is shown as given. */
  value: number | string;
  icon: LucideIcon;
  to?: string;
  hint?: string;
  className?: string;
}

export function StatCard({ label, value, icon: Icon, to, hint, className }: StatCardProps) {
  const body = (
    <CardContent className="flex items-center gap-4">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/8 text-primary">
        <Icon className="size-5" />
      </span>
      <div className="min-w-0">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-3xl font-semibold tracking-tight tabular-nums">
          {typeof value === 'number' ? formatNumber(value) : value}
        </p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </CardContent>
  );
  if (to) {
    return (
      <Card className={cn('py-5 transition-colors hover:bg-accent/40', className)}>
        <Link
          to={to}
          className="outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
        >
          {body}
        </Link>
      </Card>
    );
  }
  return <Card className={cn('py-5', className)}>{body}</Card>;
}

export function StatCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('py-5', className)}>
      <CardContent className="flex items-center gap-4">
        <Skeleton className="size-11 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-14" />
        </div>
      </CardContent>
    </Card>
  );
}
